import { MdsIconKind } from "@/design-system/components/icon";
import styled from "@emotion/styled";
import { SourceObservable } from "@/store/sources/SourceObservable";
import { observer } from "mobx-react-lite";
import { ChipVariant, MDSChip, MDSIconButton, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { MDSFloatingOverlaySlotName } from "@/mds/components/overlays/floating-overlay/floating-overlay.types";
import { MDSFloatingOverlay } from "@/mds/components/overlays/floating-overlay/floating-overlay";
import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { FloatingEmailPreviewContent } from "@/components/note/editor/source-bar/floating-email-preview";
import { FloatingTextMessagePreviewContent } from "@/components/note/editor/source-bar/floating-text-message-preview";
import { INoteObservable } from "@/store/note";
import { useMouseOverOut } from "@/domains/react/useMouseOverOut";
import { FloatingApiRequestPreviewContent } from "@/components/note/editor/source-bar/floating-api-request-preview";
import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import {
  NOTE_SOURCE_CHIP_ICON_MAP,
  NOTE_SOURCE_LABEL_MAP,
} from "@/components/note/editor/source-bar/note-editor-source-bar-chip.constants";
import { FloatingWebSitePreviewContent } from "@/components/note/editor/source-bar/floating-website-preview";
import { useNoteSourceArtifactInfo } from "@/components/note/editor/source-bar/utils/use-note-source-artifact-info";
import { useNoteSourceExtendedArtifactInfo } from "@/components/note/editor/source-bar/utils/use-note-source-extended-artifact-info";
import { truncate } from "lodash-es";
import { FloatingAudioPreviewContent } from "@/components/note/editor/source-bar/floating-audio-preview";
import {
  MdsDropdownContentList,
  MdsDropdownIconButton,
  MdsDropdownItemKind,
} from "@/design-system/components/dropdown";
import { actions } from "@/actions";
import { useAppStore } from "@/store";
import { css } from "@/domains/emotion";
import { ZIndex } from "@/domains/design/constants";
import { useRetranscribeVoiceSource } from "./note-editor-source-bar-chip.hooks";

const MAX_CHIP_LABEL_LENGTH = 36;

interface NoteEditorSourceBarChipProps {
  source: SourceObservable;
  note: INoteObservable;
  playingSourceIdRef: MutableRefObject<string | null>;
  isCollapsed: boolean;
}

export const NoteEditorSourceBarChip = observer<NoteEditorSourceBarChipProps>(
  ({ source, note, playingSourceIdRef, isCollapsed }) => {
    const { store } = useAppStore();
    const [isOpen, setIsOpen] = useState(false);
    const isDropdownMenuOpen = useRef(false);

    const updateIsOpen = useCallback(
      (isOpen: boolean, config?: { force?: boolean }) => {
        if (config?.force) {
          setIsOpen(isOpen);
          return;
        }

        if (playingSourceIdRef.current) return;
        if (isDropdownMenuOpen.current) return;

        setIsOpen(isOpen);
      },
      [playingSourceIdRef, isDropdownMenuOpen]
    );

    const { handleMouseEnter, handleMouseLeave } = useMouseOverOut({
      setIsOpen: updateIsOpen,
    });

    const artifactInfo = useNoteSourceArtifactInfo({
      sourceId: source.id,
      isOpen,
      sourceKind: source.sourceKind,
    });

    /**
     * This is a lightweight implementation for text message sources.
     * In the future, we have plans to roll out a more comprehensive solution
     * for text messages (where we can scroll through the full history).
     */
    const extendedArtifactInfo = useNoteSourceExtendedArtifactInfo({
      noteId: note.id,
      isOpen: true,
      sourceKind: source.sourceKind,
    });

    const truncatedLabel = useMemo(() => {
      if (isCollapsed) return null;
      return truncate(source.primaryLabel, {
        length: MAX_CHIP_LABEL_LENGTH,
        omission: "...",
      });
    }, [source.primaryLabel, isCollapsed]);

    const closeOverlay = useCallback(() => {
      isDropdownMenuOpen.current = false;
      updateIsOpen(false, { force: true });
    }, [updateIsOpen]);

    const { isRetranscribing, handleRetranscribe } = useRetranscribeVoiceSource({
      artifactInfo,
      onClose: closeOverlay,
    });

    const dropdownContentList: MdsDropdownContentList = useMemo(() => {
      return {
        items: [
          {
            id: "remove-source",
            kind: MdsDropdownItemKind.Button,
            label: "Remove source",
            iconKind: MdsIconKind.Trash,
            onClick: async () => {
              await actions.deleteNoteSource({
                store: store,
                noteId: note.id,
                sourceId: source.id,
              });
              closeOverlay();
            },
          },
          {
            id: "retranscribe",
            kind: MdsDropdownItemKind.Button,
            label: "Retranscribe",
            iconKind: MdsIconKind.Refresh,
            onClick: handleRetranscribe,
            isDisabled: isRetranscribing,
          },
        ],
      };
    }, [store, note.id, source.id, closeOverlay, handleRetranscribe, isRetranscribing]);

    return (
      <Wrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <MDSFloatingOverlay
          isOpen={isOpen}
          onOpenChange={updateIsOpen}
          popoverProps={{
            placement: "top",
            isNonModal: true, // Makes it work better with hover behavior
            zIndex: ZIndex.SourcesPreview,
          }}
        >
          <MDSChip
            variant={ChipVariant.Source}
            slot={MDSFloatingOverlaySlotName.Trigger}
            iconKind={NOTE_SOURCE_CHIP_ICON_MAP[source.sourceKind]}
            onPress={() => updateIsOpen(!isOpen, { force: true })}
          >
            {truncatedLabel}
          </MDSChip>
          <HeaderLeft slot={MDSFloatingOverlaySlotName.HeaderLeft}>
            <Heading>
              <StyledTitle variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
                {getSourceLabel({ artifactInfo })}
              </StyledTitle>
              <StyledSubtitle variant={MDSTextVariant.ParagraphS}>
                {getSubtitle({ artifactInfo })}
              </StyledSubtitle>
            </Heading>
          </HeaderLeft>
          <div slot={MDSFloatingOverlaySlotName.HeaderRight}>
            <StyledTopRightButtonWrapper>
              {artifactInfo?.kind === "AUDIO" && (
                <MdsDropdownIconButton
                  placement="below"
                  contentList={dropdownContentList}
                  onOpenChange={isOpen => {
                    isDropdownMenuOpen.current = isOpen;
                  }}
                  innerStyles={{
                    ContentContainer: {
                      className: css({
                        zIndex: ZIndex.FloatingActionsDropdown,
                      }),
                    },
                  }}
                />
              )}
              <MDSIconButton iconKind={MdsIconKind.Exit} onClick={closeOverlay} />
            </StyledTopRightButtonWrapper>
          </div>
          <div slot={MDSFloatingOverlaySlotName.Content}>
            {artifactInfo?.kind === "EMAIL" && (
              <FloatingEmailPreviewContent
                isLoading={artifactInfo.isLoading}
                artifactData={artifactInfo.data}
                isOpen={isOpen}
              />
            )}
            {extendedArtifactInfo?.kind === "TEXT_MESSAGE" && (
              <FloatingTextMessagePreviewContent
                isLoading={artifactInfo?.isLoading || extendedArtifactInfo.isLoading}
                isOpen={isOpen}
                extendedArtifactInfo={extendedArtifactInfo}
              />
            )}
            {artifactInfo?.kind === "API_REQUEST" && (
              <FloatingApiRequestPreviewContent
                isLoading={artifactInfo.isLoading}
                artifactData={artifactInfo.data}
              />
            )}
            {artifactInfo?.kind === "API_REQUEST_VIA_ZAPIER" && (
              <FloatingApiRequestPreviewContent
                isLoading={artifactInfo.isLoading}
                artifactData={artifactInfo.data}
              />
            )}
            {artifactInfo?.kind === "AUDIO" && (
              <FloatingAudioPreviewContent
                isLoading={artifactInfo.isLoading}
                isOpen={isOpen}
                artifactData={artifactInfo.data}
                playingSourceIdRef={playingSourceIdRef}
                sourceId={source.id}
                noteId={note.id}
                noteTitle={note.title}
                isRetranscribing={isRetranscribing}
              />
            )}
            {artifactInfo?.kind === "WEB_CLIP" && (
              <FloatingWebSitePreviewContent
                isLoading={artifactInfo.isLoading}
                artifactData={artifactInfo.data}
                isOpen={isOpen}
              />
            )}
          </div>
        </MDSFloatingOverlay>
      </Wrapper>
    );
  }
);

const getSourceLabel = ({ artifactInfo }: { artifactInfo: NoteSourceArtifactInfo | null }) => {
  if (!artifactInfo) {
    return "";
  }

  if (artifactInfo.kind === "WEB_CLIP") {
    return artifactInfo.data?.website_title;
  }

  if (artifactInfo?.kind !== "EMAIL") {
    return NOTE_SOURCE_LABEL_MAP[artifactInfo?.kind];
  }

  if (artifactInfo?.data?.sender_name) {
    return `${NOTE_SOURCE_LABEL_MAP[artifactInfo.kind]} ${artifactInfo.data?.sender_name}`;
  }

  return `${NOTE_SOURCE_LABEL_MAP[artifactInfo.kind]} ...`;
};

const getSubtitle = ({ artifactInfo }: { artifactInfo: NoteSourceArtifactInfo | null }) => {
  if (!artifactInfo) {
    return "";
  }

  if (artifactInfo.kind === "EMAIL") {
    if (artifactInfo.data?.sent_at) {
      return DateTime.fromISO(artifactInfo.data.sent_at).toFormat("MMMM d, yyyy");
    }
  }

  // TODO: Expand when we have text message data
  if (artifactInfo.kind === "TEXT_MESSAGE") {
    if (artifactInfo.data?.received_at) {
      return DateTime.fromISO(artifactInfo.data.received_at).toFormat("MMMM d, yyyy");
    }
  }

  if (artifactInfo.kind === "API_REQUEST") {
    if (artifactInfo.data?.processed_at) {
      return DateTime.fromISO(artifactInfo.data.processed_at).toFormat("MMMM d, yyyy");
    }
  }

  if (artifactInfo.kind === "API_REQUEST_VIA_ZAPIER") {
    if (artifactInfo.data?.processed_at) {
      return DateTime.fromISO(artifactInfo.data.processed_at).toFormat("MMMM d, yyyy");
    }
  }

  if (artifactInfo.kind === "WEB_CLIP") {
    return artifactInfo.data?.website_url;
  }

  return "";
};

const Wrapper = styled.div``;

const HeaderLeft = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`
);

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  text-overflow: ellipsis;
  max-width: min(500px, 90vw) !important;
`;

const StyledTitle = styled(MDSText)(
  ({ theme }) => `
  font-size: ${theme.fontSizes.medium};
  font-weight: ${theme.fontWeights.semiBold};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`
);

const StyledSubtitle = styled(MDSText)(
  ({ theme }) => `
  font-size: ${theme.fontSizes.xxsmall};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`
);

const StyledTopRightButtonWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: ${theme.spacing.xs};
`
);
